// Privacy Policy page layout data — identical shape to
// termsPageLayouts.constants.js (see that file's header comment for the
// full rationale: single layout, HTML content seeded from the real live
// page, ru/ar left unseeded).
//
// `content`'s `en` seed is meelz-client-v2's real, live Privacy Policy copy
// (src/app/[locale]/(app)/privacy/page.tsx), converted verbatim to HTML.
// The page subtitle ("RPT Restaurant LLC – Healthy Meal Delivery Service
// (Meelz)") is folded into the content itself as the first paragraph,
// since there is no separate subtitle field in this schema (out of scope
// per the plan — title/subtitle fields were not requested).

const PRIVACY_PAGE_CONTENT_EN = `
<p>RPT Restaurant LLC – Healthy Meal Delivery Service (Meelz)</p>
<h2>1. General Information</h2>
<p>This Privacy Policy explains how RPT Restaurant LLC, a company incorporated under the laws of the United Arab Emirates (hereinafter referred to as the "Company", "Provider", or "we"), collects, uses, stores, and protects personal information of users and clients of the website <a href="https://meelz.ae">https://meelz.ae</a> and related services.</p>
<p>We respect your privacy and are committed to protecting your personal data in accordance with applicable laws of the United Arab Emirates, including the UAE Personal Data Protection Law where applicable.</p>
<p>By using our website, placing an order, or communicating with us through any official channel, you agree to the terms of this Privacy Policy.</p>
<h2>2. Information We Collect</h2>
<p>When you place an order through our website, messaging platforms, or phone, we may collect the following information:</p>
<ul>
<li>Full name</li>
<li>Phone number</li>
<li>Delivery address</li>
<li>Email address</li>
<li>Payment information (processed through secure third-party payment providers)</li>
<li>Dietary preferences, nutritional goals, or comments</li>
<li>Order history and service preferences</li>
<li>Any additional information necessary to provide our services</li>
</ul>
<p>When you visit our website, we may also automatically collect certain technical information such as:</p>
<ul>
<li>IP address</li>
<li>Device type</li>
<li>Browser information</li>
<li>Website usage data through cookies or analytics tools</li>
</ul>
<h2>3. Purpose of Data Collection</h2>
<p>We collect and use personal data for the following purposes:</p>
<ul>
<li>Processing and delivering your meal plan orders</li>
<li>Communicating with you regarding your orders and deliveries</li>
<li>Customer support and service improvements</li>
<li>Managing payments and billing</li>
<li>Sending promotional offers, updates, or newsletters only with your consent</li>
</ul>
<p>Your personal data will never be used for purposes unrelated to our services.</p>
<h2>4. Sharing of Information with Third Parties</h2>
<p>We do not sell or trade personal data to third parties.</p>
<p>However, we may share necessary information with trusted service providers solely for operational purposes, including:</p>
<ul>
<li>Delivery partners and couriers (for order delivery)</li>
<li>Payment processors (such as Stripe or other licensed payment gateways)</li>
<li>IT service providers supporting website operations</li>
</ul>
<p>We may also disclose personal data if required by law, regulation, or request from competent government authorities in the UAE.</p>
<h2>5. Data Storage and Protection</h2>
<p>We implement reasonable administrative, technical, and security measures to protect personal information from unauthorized access, misuse, or disclosure.</p>
<p>Personal data is stored on secure systems and is accessible only to authorized personnel.</p>
<p>We retain personal data only for as long as necessary to:</p>
<ul>
<li>Provide our services</li>
<li>Comply with legal obligations</li>
<li>Resolve disputes or enforce agreements</li>
</ul>
<h2>6. Marketing Communications</h2>
<p>With your consent, we may send you information about:</p>
<ul>
<li>Special promotions</li>
<li>New meal programs</li>
<li>Company updates</li>
</ul>
<p>You may unsubscribe or opt out of promotional communications at any time.</p>
<h2>7. Your Rights</h2>
<p>Subject to applicable laws, you may have the right to:</p>
<ul>
<li>Request access to your personal data</li>
<li>Request correction of inaccurate information</li>
<li>Request deletion of your personal data</li>
<li>Withdraw consent to marketing communications</li>
</ul>
<p>Requests may be submitted using the contact information below.</p>
<h2>8. Cookies and Website Analytics</h2>
<p>Our website may use cookies and similar technologies to improve user experience, analyze website performance, and understand user behavior.</p>
<p>You may disable cookies in your browser settings, although certain website features may not function properly as a result.</p>
<h2>9. Contact Information</h2>
<p>If you have any questions about this Privacy Policy or the way we handle personal data, you may contact us:</p>
<p><strong>RPT Restaurant LLC</strong></p>
<p>Phone: <a href="tel:+971501097972">+971 50 109 7972</a></p>
<p>Website: <a href="https://meelz.ae">https://meelz.ae</a></p>
<h2>10. Policy Updates</h2>
<p>This Privacy Policy may be updated periodically.</p>
<p>Any changes will be published on this page, and the updated version shall become effective upon publication.</p>
`.trim();

export const PRIVACY_PAGE_DEFAULT_PROPS = {
    backgroundColor: "#ffffff",
    textColor:       "#1E1E1E",
    content:         { en: PRIVACY_PAGE_CONTENT_EN },
};

// CK: same rationale as termsPageLayouts.constants.js's own CK entry — see
// that file's header comment for the full explanation.
export const PRIVACY_PAGE_CK_DEFAULT_PROPS = {
    backgroundColor: "#ffffff",
    textColor:       "#46298A",
    content:         { en: PRIVACY_PAGE_CONTENT_EN },
};

// Single "default" layout id only — no layout picker/dropdown shown (there
// is nothing to choose between; every brand just gets its own real content
// automatically). CK's real content (PRIVACY_PAGE_CK_DEFAULT_PROPS above)
// used to be reachable only via a separate, easily-mis-selected
// "Privacy Policy (CK)" layout option here — removed in favor of
// FULL_CONTENT_OVERRIDE_BY_KEY_AND_BRAND (componentRegistry.js), same
// pattern already used for faq/quiz's own former "(CK)" layout options.
export const PRIVACY_PAGE_LAYOUTS = {
    default: { label: "Privacy Policy", defaultProps: PRIVACY_PAGE_DEFAULT_PROPS },
};

export const PRIVACY_PAGE_SCHEMA = [
    { key: "content",         label: "Page content",     type: "richText", translatable: true },
    { key: "backgroundColor", label: "Background color", type: "color" },
    { key: "textColor",       label: "Text color",       type: "color" },
];
